import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  EventEmitter,
  Input,
  OnInit,
  Output,
  signal,
  viewChildren,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  CartItem,
  CartItemInterface,
  ProductReference,
  Specification,
  SpecificationMap,
  Variant,
  YsoProduct,
} from '../../../../data/model';
import {SeFeFormFieldSelectComponent} from 'se-fe-form-field-select';

interface ProductOption {
  label: string;
  value: string;
}

interface SpecOption {
  specId: string;
  optionId: string;
}

export interface YsoProductForm {
  productId: FormControl<string | null>;
  quantity: FormControl<number | null>;
  specs?: FormArray<FormControl<SpecOption | null>>;
  references: FormControl<ProductReference | null>;
}

@Component({
  selector: 'app-yso-product-form',
  templateUrl: './yso-product-form.component.html',
  styleUrls: ['./yso-product-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {class: 'app-yso-product-form'},
  standalone: false,
})
export class YsoProductFormComponent implements OnInit {
  @Input() showQuantity = true;
  @Input() isBundle = false;

  @Output() formReady = new EventEmitter<boolean>();

  productForm: FormGroup<YsoProductForm>;
  ready = false;
  selectControls = viewChildren(SeFeFormFieldSelectComponent);
  specOptions: {[specId: string]: {label: string; value: string}[]} = {};
  specsValue = signal<SpecOption[]>([]);

  variants = computed(() => this._variants());

  selectedVariant = computed(() => {
    const selectedSpecs = this.specsValue();
    const specs = this.specs();
    if (!specs || specs.length === 0) return undefined;
    if (selectedSpecs.length === 0) return undefined;

    // Filter out entries that don't have an optionId selected yet
    const filteredSelectedSpecs = selectedSpecs.filter(s => !!s.optionId);
    if (filteredSelectedSpecs.length !== specs.length) return undefined;

    const variantsToSearch =
      this.product.variants && this.product.variants.length > 0
        ? this.product.variants
        : this.variants();

    // Match variant by specs (works for both Items and Products controllers)
    const matchedVariant = variantsToSearch.find(variant => {
      const variantSpecs = variant.specs || [];
      if (variantSpecs.length !== filteredSelectedSpecs.length) return false;
      if (variantSpecs.length === 0) return false;

      return variantSpecs.every(spec => {
        return filteredSelectedSpecs.some(
          selected =>
            String(selected.specId) === String(spec.specId) &&
            String(selected.optionId) === String(spec.optionId),
        );
      });
    });

    return matchedVariant;
  });

  specs = computed(() => {
    // Prefer specificationMap (fetched data) over product.specifications (embedded data)
    const specsFromMap = this.specificationMap?.[this.product.id];
    if (specsFromMap && specsFromMap.length > 0) {
      return specsFromMap;
    }
    if (this.product.specifications && this.product.specifications.length > 0) {
      return this.product.specifications;
    }
    return undefined;
  });

  validOptionGroupings = computed(() => {
    // Prefer variants from variantMap (fetched data) over product.variants (embedded data)
    const variantsFromMap = this.variants();
    const variantsToSearch =
      variantsFromMap && variantsFromMap.length > 0
        ? variantsFromMap
        : this.product.variants && this.product.variants.length > 0
          ? this.product.variants
          : [];

    return variantsToSearch.map(v => {
      return v.specs?.map(s => s.optionId) || [];
    });
  });

  private _product = signal<YsoProduct>({} as YsoProduct);
  private _specificationMap = signal<SpecificationMap>({});
  private _variants = signal<Variant[]>([]);

  constructor(private changeDetector: ChangeDetectorRef) {
    this.productForm = new FormGroup<YsoProductForm>({
      productId: new FormControl<string>(''),
      quantity: new FormControl(1, [Validators.required]),
      specs: new FormArray<FormControl<SpecOption | null>>([]),
      references: new FormControl(null),
    });

    this.productForm.statusChanges.subscribe(() => {
      this.formReady.emit(this.valid);
    });

    this.specsFormGroupArray.valueChanges.subscribe(() => {
      this.specsValue.set(this.specsFormGroupArray.value);
      this.formReady.emit(this.valid);
    });

    effect(() => {
      const product = this.product;
      if (!product || !product.id) return;

      const specs = this.specs();
      // Prefer variants from variantMap over product.variants
      const variantsFromMap = this.variants();
      const variants =
        variantsFromMap && variantsFromMap.length > 0
          ? variantsFromMap
          : product.variants && product.variants.length > 0
            ? product.variants
            : [];

      // If we expect specs but don't have them or variants yet, wait.
      if (
        !this.ready &&
        (!specs || specs.length === 0 || !variants || variants.length === 0)
      ) {
        // Check if it's a legacy product that needs fetching.
        const isLegacyMissingData =
          (!product.variants || product.variants.length === 0) &&
          (!product.specifications || product.specifications.length === 0);

        if (isLegacyMissingData) {
          return;
        }
      }

      if (specs && specs.length > 0 && variants && variants.length > 0) {
        this.buildSpecFormControls();

        this.specsFormGroupArray.controls.forEach(c => {
          c.valueChanges.subscribe(v => this.specsChanged(v));
        });

        this.ready = true;
        this.formReady.emit(this.valid);
        this.changeDetector.detectChanges();
        this.changeDetector.markForCheck();
      } else if (product.id && (!specs || specs.length === 0) && !this.ready) {
        // Check if it's a legacy product that needs fetching.
        const isLegacyMissingData =
          (!product.variants || product.variants.length === 0) &&
          (!product.specifications || product.specifications.length === 0);

        if (isLegacyMissingData) {
          return;
        }

        // If no specs, it's a simple product, mark ready
        this.ready = true;
        this.formReady.emit(this.valid);
        this.changeDetector.detectChanges();
        this.changeDetector.markForCheck();
      }
    });
  }

  get product(): YsoProduct {
    return this._product();
  }

  get specificationMap(): SpecificationMap {
    return this._specificationMap();
  }

  get valid(): boolean {
    return this.productForm.valid;
  }

  get value(): CartItemInterface {
    const item = new CartItem(<CartItemInterface>this.productForm.value);
    item.fulfilledBy = this.product.fulfillment;
    item.itemVariationId = this.selectedVariant()?.id;
    item.unitPrice = this.currentPrice;
    item.templateKey = this.product.templateKey;
    return item;
  }

  get currentPrice(): number {
    const variant = this.selectedVariant();
    return variant?.priceCents ? variant.priceCents / 100 : this.product.price;
  }

  get referencesControl(): FormControl<ProductReference> {
    return this.productForm.get('references') as FormControl<ProductReference>;
  }

  get productIdControl(): FormControl<string> {
    return this.productForm.get('productId') as FormControl<string>;
  }

  get quantityControl(): FormControl<number> {
    return this.productForm.get('quantity') as FormControl<number>;
  }

  get specsFormGroupArray(): FormArray {
    return this.productForm.get('specs') as FormArray;
  }

  @Input() set product(value: YsoProduct) {
    this._product.set(value);
  }

  @Input() set specificationMap(value: SpecificationMap) {
    this._specificationMap.set(value);
  }

  @Input() set variantsInput(value: Variant[]) {
    this._variants.set(value);
  }

  ngOnInit(): void {
    this.productIdControl.setValue(this.product.id);
    this.referencesControl.setValue(this.product.references);
  }

  specsChanged(selectedOption: SpecOption): void {
    const groupings = this.validOptionGroupings();
    if (groupings.length === 0) return;

    for (const selectControl of this.selectControls()) {
      if (
        selectedOption &&
        selectControl['elementRef'].nativeElement.id ===
          String(selectedOption.specId)
      )
        continue;

      for (const option of selectControl.options) {
        const optionId = String(option.value);

        const specOption = groupings.find(
          o =>
            o.map(String).includes(optionId) &&
            o.map(String).includes(String(selectedOption.optionId)),
        );
        option.disabled = !specOption && !!selectedOption.optionId;
      }
    }
  }

  getControlLabel(spec: {specId: string}): string {
    const s = this.specs()?.find(s => String(s.id) === String(spec.specId));
    let name = s?.name ?? '';
    if (name.toLowerCase() === 'colors') name = 'Color';
    if (name.toLowerCase() === 'sizes') name = 'Size';
    return name;
  }

  getPlaceholder(spec: {specId: string}): string {
    const label = this.getControlLabel(spec).toLowerCase();
    if (label === 'size') return 'Choose a size';
    if (label === 'color') return 'Choose a color';
    return 'Choose an option';
  }

  getSeFeDataOptions(spec: Specification): ProductOption[] {
    const validGroupings = this.validOptionGroupings();
    if (validGroupings.length === 0) {
      return spec.options.map(o => ({
        value: String(o.id),
        label: String(o.value),
      }));
    }

    return spec.options.map(option => {
      const optionId = String(option.id);
      const productOption: ProductOption = {
        value: optionId,
        label: String(option.value),
      };

      return productOption;
    });
  }

  /**
   * Attempts to match a variant by checking if its name contains the selected option values.
   * This is a heuristic approach used when variants don't have spec information.
   */
  matchVariantByName(
    variants: Variant[],
    selectedSpecs: SpecOption[],
    specs: Specification[],
  ): Variant | undefined {
    // Build a map of selected option values by getting the actual option text
    const selectedValues: string[] = [];

    for (const selectedSpec of selectedSpecs) {
      const spec = specs.find(
        s => String(s.id) === String(selectedSpec.specId),
      );
      if (spec) {
        const option = spec.options.find(
          o => String(o.id) === String(selectedSpec.optionId),
        );
        if (option?.value) {
          selectedValues.push(option.value.toLowerCase());
        }
      }
    }

    // If we couldn't get any option values, can't match
    if (selectedValues.length === 0) {
      return undefined;
    }

    // Try to find a variant whose name contains all selected values
    return variants.find(variant => {
      if (!variant.name) return false;

      const variantName = variant.name.toLowerCase();
      // Check if variant name contains all selected option values
      return selectedValues.every(value => variantName.includes(value));
    });
  }

  buildSpecFormControls(): void {
    // Clear existing form controls first
    while (this.specsFormGroupArray.length > 0) {
      this.specsFormGroupArray.removeAt(0);
    }

    this.specOptions = {};
    this.specs()?.forEach(spec => {
      this.specOptions[String(spec.id)] = this.getSeFeDataOptions(spec);

      const formGroup = new FormGroup({
        specId: new FormControl(String(spec.id)),
        optionId: new FormControl('', [Validators.required]),
      });
      this.specsFormGroupArray.push(formGroup);
    });
  }

  resetFormControls(): void {
    this.quantityControl.reset();
    this.quantityControl.setValue(1);
    this.#resetOptionsFormControls();
  }

  displayControlErrors(control: AbstractControl): boolean {
    return control.invalid && control.dirty;
  }

  setOptionsDirtyFlagTrue(): void {
    this.specsFormGroupArray.controls.forEach(control => {
      const optionControl = control.get('optionId');
      if (optionControl) {
        optionControl.markAsTouched();
        optionControl.markAsDirty();
      }
    });
    this.changeDetector.detectChanges();
  }

  #resetOptionsFormControls(): void {
    this.specsFormGroupArray.controls.forEach(control => {
      const optionControl = control.get('optionId');
      if (optionControl) {
        optionControl.reset();
      }
    });
  }
}
